import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { useProjects } from "@/hooks/use-projects";
import { useAccounts } from "@/hooks/use-accounts";
import {
  POST_TYPES,
  POST_TYPE_META,
  type PostType,
} from "@/hooks/use-post-generation";
import { PlatformIcon } from "@/components/ui/platform-icon";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PostTypeEditor, type PieceDraft } from "@/components/composers/PostTypeEditor";
import { apiFetch } from "@/lib/api-client";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Zap } from "lucide-react";

const TIER_LABELS: Record<1 | 2 | 3, string> = {
  1: "Tier 1 — single image / caption",
  2: "Tier 2 — multi-media",
  3: "Tier 3 — video",
};

const EMPTY_DRAFT: PieceDraft = {
  caption: "",
  title: null,
  description: null,
  hashtags: [],
  threadItems: null,
  platformKnobs: {},
  mediaUrls: [],
};

export function BrandQuickPostProPage() {
  const { t } = useTranslation("compose");
  const { brand } = useBrandContext();
  const navigate = useNavigate();
  const accountsQ = useAccounts(brand.id);
  const projectsQ = useProjects(brand.id);

  const [selectedPostType, setSelectedPostType] = useState<PostType | null>(null);
  const [draft, setDraft] = useState<PieceDraft>(EMPTY_DRAFT);
  const [projectId, setProjectId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  // Connected platforms — derive from active SocialAccounts
  const connectedPlatforms = useMemo(() => {
    const set = new Set<string>();
    for (const a of accountsQ.data ?? []) {
      if (a.status === "ACTIVE") set.add(a.platform);
    }
    return set;
  }, [accountsQ.data]);

  // Available PostTypes grouped by tier + platform
  const availablePostTypes = useMemo(() => {
    const result: Array<{
      tier: 1 | 2 | 3;
      label: string;
      byPlatform: Record<string, PostType[]>;
    }> = [];
    for (const tier of [1, 2, 3] as const) {
      const byPlatform: Record<string, PostType[]> = {};
      for (const pt of POST_TYPES) {
        const meta = POST_TYPE_META[pt];
        if (meta.tier !== tier) continue;
        if (!connectedPlatforms.has(meta.platform)) continue;
        if (!byPlatform[meta.platform]) byPlatform[meta.platform] = [];
        byPlatform[meta.platform].push(pt);
      }
      if (Object.keys(byPlatform).length > 0) {
        result.push({ tier, label: TIER_LABELS[tier], byPlatform });
      }
    }
    return result;
  }, [connectedPlatforms]);

  const handleSavePiece = async (status: "DRAFT" | "PENDING_REVIEW") => {
    if (!selectedPostType) return;
    if (!projectId) {
      // Resolve or create General — match server behavior
      const general = (projectsQ.data ?? []).find((p: any) => p.title === "General");
      setProjectId(general?.id ?? "");
    }
    setSubmitting(true);
    try {
      // Resolve project (General fallback)
      let effectiveProjectId = projectId;
      if (!effectiveProjectId) {
        const general = (projectsQ.data ?? []).find((p: any) => p.title === "General");
        if (general) effectiveProjectId = general.id;
      }
      if (!effectiveProjectId) {
        // Create General on the fly — small UX nicety so first-time users don't get blocked
        const created = await apiFetch<{ id: string }>(
          `/brands/${brand.id}/projects`,
          {
            method: "POST",
            body: JSON.stringify({
              title: "General",
              brief: "Default project for ad-hoc posts.",
              targetPlatforms: [],
              targetFormats: [],
            }),
          },
        );
        effectiveProjectId = created.id;
      }

      const res = await apiFetch<{ id: string }>(
        `/brands/${brand.id}/projects/${effectiveProjectId}/pieces`,
        {
          method: "POST",
          body: JSON.stringify({
            postType: selectedPostType,
            caption: draft.caption,
            copy: draft.caption,
            hashtags: draft.hashtags,
            mediaUrls: draft.mediaUrls,
            title: draft.title,
            description: draft.description,
            threadItems: draft.threadItems,
            platformKnobs: draft.platformKnobs,
            status,
          }),
        },
      );
      toast.success(
        status === "PENDING_REVIEW"
          ? t("quickPostPro.submittedForReview")
          : t("quickPostPro.savedAsDraft"),
      );
      navigate(
        `/${brand.slug}/review${status === "PENDING_REVIEW" ? "?status=PENDING_REVIEW" : "?status=DRAFT"}`,
      );
      void res;
    } catch (err: any) {
      toast.error(err?.message ?? t("quickPostPro.failedToSave"));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── No platforms connected ──────────────────────────────
  if (accountsQ.isLoading) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto w-full space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }
  if (connectedPlatforms.size === 0) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto w-full space-y-4">
        <header>
          <h1 className="text-2xl font-semibold">{t("quickPostPro.title")}</h1>
        </header>
        <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
          {t("quickPostPro.noConnectedPlatforms")}{" "}
          <a href={`/${brand.slug}/settings/accounts`} className="underline">
            {t("quickPostPro.settingsAccountsLink")}
          </a>{" "}
          {t("quickPostPro.noConnectedPlatformsSuffix")}
        </div>
      </div>
    );
  }

  // ─── Step 1: PostType picker ──────────────────────────────
  if (!selectedPostType) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto w-full space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Zap className="h-6 w-6 text-amber-500" />
            {t("quickPostPro.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("quickPostPro.subtitle")}
          </p>
          <div className="text-xs text-muted-foreground">
            {t("quickPostPro.composeLinkPrefix")}{" "}
            <a className="underline" href={`/${brand.slug}/compose`}>
              {t("quickPostPro.composeLink")}
            </a>
          </div>
        </header>

        <div className="space-y-5">
          {availablePostTypes.map((g) => (
            <section key={g.tier} className="space-y-3">
              <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                {g.label}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(g.byPlatform).map(([platform, types]) => (
                  <div key={platform} className="rounded-lg border p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <PlatformIcon platform={platform} size={20} />
                      <span className="text-sm font-medium">{platform.replace(/_/g, " ")}</span>
                    </div>
                    <div className="space-y-1">
                      {types.map((pt) => (
                        <button
                          key={pt}
                          type="button"
                          onClick={() => {
                            setSelectedPostType(pt);
                            setDraft({
                              ...EMPTY_DRAFT,
                              threadItems:
                                pt === "X_THREAD" || pt === "THREADS_THREAD"
                                  ? [{ content: "", mediaUrls: [] }]
                                  : null,
                            });
                          }}
                          className="w-full text-left rounded px-2 py-1.5 text-sm hover:bg-muted transition-colors flex items-center justify-between gap-2"
                        >
                          <span>{POST_TYPE_META[pt].label}</span>
                          <span className="text-[10px] text-muted-foreground">{pt}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    );
  }

  // ─── Step 2: Editor ──────────────────────────────────────
  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => {
            setSelectedPostType(null);
            setDraft(EMPTY_DRAFT);
          }}
          className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("quickPostPro.backToTypes")}
        </button>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">{t("quickPostPro.project")}</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value="">{t("fields.projectDefault")}</option>
            {(projectsQ.data ?? []).map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <PostTypeEditor
        brandId={brand.id}
        postType={selectedPostType}
        initial={draft}
        brandHandle={brand.slug}
        brandName={brand.name}
        onSave={async (changes) => {
          setDraft((d) => ({ ...d, ...changes }));
        }}
        disabled={submitting}
      />

      <div className="flex items-center justify-end gap-2 sticky bottom-4 bg-background/90 backdrop-blur py-2 -mx-2 px-2 rounded">
        <Button
          variant="outline"
          disabled={submitting}
          onClick={() => handleSavePiece("DRAFT")}
        >
          {t("quickPostPro.saveAsDraft")}
        </Button>
        <Button
          disabled={submitting}
          onClick={() => handleSavePiece("PENDING_REVIEW")}
          className="gap-1"
        >
          <Sparkles className="h-4 w-4" />
          {t("quickPostPro.submitForReview")}
        </Button>
      </div>
    </div>
  );
}
